import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCalendar,
  FiDollarSign,
  FiFilter,
  FiPackage,
  FiPieChart,
  FiRefreshCcw,
  FiShoppingCart,
  FiTrendingUp,
  FiUsers,
  FiArrowRight,
  FiBarChart2,
  FiTruck,
  FiMonitor,
  FiFileText,
  FiBriefcase,
  FiCreditCard,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import './analytics.css';

const COLORS = ["#7A2238", "#D4AF37", "#5A182A", "#9F4A64", "#2563EB"];

function indiaDate() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
  }).format(new Date());
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
}

function comparisonMeta(value, { inverse = false } = {}) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return {
      label: "No comparable data last year",
      className: "text-gray-400",
    };
  }

  const percentage = Number(value);
  const isPositiveOutcome = inverse ? percentage <= 0 : percentage >= 0;

  return {
    label: `${percentage >= 0 ? "+" : ""}${percentage.toFixed(1)}% vs last year`,
    className: isPositiveOutcome ? "text-green-600" : "text-red-600",
  };
}

function getRange(range) {
  const today = indiaDate();
  const current = new Date(`${today}T00:00:00.000Z`);

  if (range === "Today") {
    return { from: today, to: today };
  }

  if (range === "This Week") {
    const weekStart = new Date(current);
    const day = weekStart.getUTCDay();
    const daysSinceMonday = (day + 6) % 7;

    weekStart.setUTCDate(weekStart.getUTCDate() - daysSinceMonday);

    return {
      from: weekStart.toISOString().slice(0, 10),
      to: today,
    };
  }

  if (range === "This Year") {
    return {
      from: `${current.getUTCFullYear()}-01-01`,
      to: today,
    };
  }

  return {
    from: `${today.slice(0, 7)}-01`,
    to: today,
  };
}

function trendLabel(value) {
  if (!value) return "";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
  }).format(new Date(`${value}T00:00:00.000Z`));
}

export default function ReportsDashboard() {
  const { accessToken, user } = useAuth();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState("This Month");
  const [storeId, setStoreId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [customFrom, setCustomFrom] = useState(indiaDate());
  const [customTo, setCustomTo] = useState(indiaDate());
  const [productFilters,setProductFilters] = useState({});
  const [filterOptions,setFilterOptions] = useState({});

  const [dashboard, setDashboard] = useState(null);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState("");

  const selectedRange = useMemo(() => dateRange === 'Custom' ? { from: customFrom, to: customTo } : dateRange === 'Financial Year' ? { from: `${Number(indiaDate().slice(0,4)) - (Number(indiaDate().slice(5,7)) < 4 ? 1 : 0)}-04-01`, to: indiaDate() } : getRange(dateRange), [dateRange, customFrom, customTo]);

  const loadFilters = useCallback(async () => {
    if (!accessToken) return;

    try {
      const options = await apiRequest('/reports/dashboard/filters',{accessToken});
      setStores(options.stores || []);
      setCategories(options.categories || []);
      setFilterOptions(options);
    } catch {
      // Dashboard still works even when optional filter data is unavailable.
    }
  }, [accessToken]);

  const loadDashboard = useCallback(
    async (showRefresh = false) => {
      if (!accessToken) return;

      try {
        if (showRefresh) {
          setIsRefreshing(true);
        } else {
          setIsLoading(true);
        }

        setError("");

        const query = new URLSearchParams({
          from: selectedRange.from,
          to: selectedRange.to,
        });

        if (storeId) query.set("storeId", storeId);
        if (categoryId) query.set("categoryId", categoryId);
        if (paymentMethod) query.set("paymentMethod", paymentMethod);
        Object.entries(productFilters).forEach(([key,value])=>{if(value)query.set(key,value);});

        const response = await apiRequest(
          `/reports/dashboard?${query.toString()}`,
          { accessToken },
        );

        setDashboard(response);
      } catch (requestError) {
        setError(requestError.message || "Unable to load report dashboard.");
        setDashboard(null);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [
      accessToken,
      selectedRange.from,
      selectedRange.to,
      storeId,
      categoryId,
      paymentMethod,
      productFilters,
    ],
  );

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const exportDashboard = () => {
    if (!dashboard) return;

    const rows = [
      ["Report", "Value"],
      ["Period", `${selectedRange.from} to ${selectedRange.to}`],
      ["Net Revenue", dashboard.metrics.revenue],
      ["Gross Revenue", dashboard.metrics.grossRevenue],
      ["Returns Amount", dashboard.metrics.returnAmount],
      ["Orders", dashboard.metrics.orders],
      ["Customers", dashboard.metrics.customers],
      ["Products Sold", dashboard.metrics.productsSold],
      ["Returns", dashboard.metrics.returns],
      ["Average Order", dashboard.metrics.averageOrder],
      ["Previous Year Revenue", dashboard.comparison?.previous?.revenue],
      ["Revenue Change %", dashboard.comparison?.changes?.revenue],
      ["Previous Year Orders", dashboard.comparison?.previous?.orders],
      ["Orders Change %", dashboard.comparison?.changes?.orders],
      ["Previous Year Customers", dashboard.comparison?.previous?.customers],
      ["Customers Change %", dashboard.comparison?.changes?.customers],
    ];

    const csv = rows
      .map((row) =>
        row
          .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `charms-report-${selectedRange.from}-to-${selectedRange.to}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const metrics = dashboard?.metrics || {};
  const comparisonChanges = dashboard?.comparison?.changes || {};

  const salesTrend = (dashboard?.salesTrend || []).map((item) => ({
    ...item,
    label: trendLabel(item.date),
  }));

  const stats = [
    {
      title: "Net Revenue",
      value: formatMoney(metrics.revenue),
      detail: "POS revenue after returns",
      comparison: comparisonMeta(comparisonChanges.revenue),
      icon: <FiDollarSign size={24} />,
      color: "bg-green-500",
    },
    {
      title: "Orders",
      value: formatNumber(metrics.orders),
      detail: "POS transactions",
      comparison: comparisonMeta(comparisonChanges.orders),
      icon: <FiShoppingCart size={24} />,
      color: "bg-blue-500",
    },
    {
      title: "Customers",
      value: formatNumber(metrics.customers),
      detail: "Unique purchasing customers",
      comparison: comparisonMeta(comparisonChanges.customers),
      icon: <FiUsers size={24} />,
      color: "bg-purple-500",
    },
    {
      title: "Products Sold",
      value: formatNumber(metrics.productsSold),
      detail: "Units sold",
      comparison: comparisonMeta(comparisonChanges.productsSold),
      icon: <FiPackage size={24} />,
      color: "bg-orange-500",
    },
    {
      title: "Returns Value",
      value: formatMoney(metrics.returnAmount),
      detail: `${formatNumber(metrics.returns)} return transactions`,
      comparison: comparisonMeta(comparisonChanges.returns, {
        inverse: true,
      }),
      icon: <FiRefreshCcw size={24} />,
      color: "bg-red-500",
    },
    {
      title: "Average Order",
      value: formatMoney(metrics.averageOrder),
      detail: "Net revenue per order",
      comparison: comparisonMeta(comparisonChanges.averageOrder),
      icon: <FiPieChart size={24} />,
      color: "bg-indigo-500",
    },
    {
      title: "Gross Revenue",
      value: formatMoney(metrics.grossRevenue),
      detail: "POS revenue before returns",
      comparison: {
        label: "Selected period",
        className: "text-gray-400",
      },
      icon: <FiTrendingUp size={24} />,
      color: "bg-emerald-500",
    },
    {
      title: "Return Value Rate",
      value: metrics.grossRevenue > 0 ? `${(Number(metrics.returnAmount || 0) / Number(metrics.grossRevenue) * 100).toFixed(1)}%` : '—',
      detail: "Return value as a share of gross revenue",
      comparison: {
        label: "Selected period",
        className: "text-gray-400",
      },
      icon: <FiFilter size={24} />,
      color: "bg-yellow-500",
    },
  ];

  const channelStats = [
    ['Posted GRNs',metrics.purchases?.receipts,false,'Posted purchase receipts'],
    ['Purchase Value',metrics.purchases?.value,true,'Received goods including GST'],
    ['Accepted Purchase Units',metrics.purchases?.acceptedQuantity,false,'Units accepted into inventory'],
    ['Rejected Purchase Units',metrics.purchases?.rejectedQuantity,false,'Units rejected on receipt'],
    ['Purchase Input GST',metrics.purchases?.inputGst,true,'GST on received goods'],
    ['Purchase Freight',metrics.purchases?.freight,true,'Before GST; all categories only'],
    ['Supplier Return Value',metrics.purchases?.returnValue,true,'Dispatched supplier returns'],
    ['Online Orders',metrics.ecommerce?.orders,false,'All online orders placed'],
    ['Online Order Value',metrics.ecommerce?.value,true,'Excludes cancelled and failed orders'],
    ['Online Paid Value',metrics.ecommerce?.paidOrderValue,true,'Current paid-order value'],
    ['Online Pending Payment',metrics.ecommerce?.pendingPaymentValue,true,'Current pending-order value'],
    ['Online Average Order',metrics.ecommerce?.averageOrder,true,'Active order value per order'],
    ['Online Cancelled Orders',metrics.ecommerce?.cancelled,false,'Cancelled order count'],
    ['Online Delivered Orders',metrics.ecommerce?.delivered,false,'Delivered order count'],
    ['Online Awaiting Fulfilment',metrics.ecommerce?.awaitingFulfilment,false,'Confirmed, processing or partly shipped'],
  ].map(([title,value,currency,detail], index)=>({ title, value: value == null ? '—' : currency ? formatMoney(value) : formatNumber(value), detail, comparison: { label: 'Selected period', className: 'text-gray-400' }, icon: index < 7 ? <FiTruck size={24}/> : <FiShoppingCart size={24}/>, color: index < 7 ? 'bg-orange-500' : 'bg-indigo-500' }));

  return (
    <div className="charms-analytics min-h-screen space-y-6">
      <div className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-primary-700">Business intelligence</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 lg:text-3xl">
            Analytics Dashboard
          </h1>

          <p className="mt-2 text-gray-500">
            Sales, purchases, ecommerce and inventory at a glance.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => loadDashboard(true)}
            disabled={isRefreshing}
            className="flex items-center gap-2 rounded-2xl border bg-white px-5 py-3 shadow-sm hover:shadow disabled:opacity-50"
          >
            <FiRefreshCcw className={isRefreshing ? "animate-spin" : ""} />
            Refresh
          </button>

        </div>
      </div>

      {error ? (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          <FilterSelect
            label="Date Range"
            value={dateRange}
            onChange={setDateRange}
            options={["Today", "This Week", "This Month", "This Year", "Financial Year", "Custom"]}
          />

          <FilterSelect
            label="Store"
            value={storeId}
            onChange={setStoreId}
            options={[
              { value: "", label: "All Stores" },
              ...stores
                .filter((store) => store.isActive)
                .map((store) => ({
                  value: store.id,
                  label: `${store.name} (${store.code})`,
                })),
            ]}
          />

          <FilterSelect
            label="Category"
            value={categoryId}
            onChange={setCategoryId}
            options={[
              { value: "", label: "All Categories" },
              ...categories.map((category) => ({
                value: category.id,
                label: category.name,
              })),
            ]}
          />

          <FilterSelect
            label="Payment"
            value={paymentMethod}
            onChange={setPaymentMethod}
            options={[
              { value: "", label: "All Payment Methods" },
              { value: "CASH", label: "Cash" },
              { value: "UPI", label: "UPI" },
              { value: "CARD", label: "Card" },
              { value: "BANK_TRANSFER", label: "Bank Transfer" },
              { value: "STORE_CREDIT", label: "Store Credit" },
              { value: "OTHER", label: "Other" },
            ]}
          />
        </div>

        {dateRange === 'Custom' && <div className="mt-4 flex gap-4"><label className="text-sm">From<input type="date" value={customFrom} max={customTo} onChange={e=>setCustomFrom(e.target.value)} className="ml-2 rounded-xl border p-2" /></label><label className="text-sm">To<input type="date" value={customTo} min={customFrom} onChange={e=>setCustomTo(e.target.value)} className="ml-2 rounded-xl border p-2" /></label></div>}
        <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[['brandId','Brand','brands'],['subCategoryId','Sub category','subCategories'],['seasonId','Season','seasons'],['productId','Product','products'],['colorId','Colour','colors'],['sizeId','Size','sizes']].map(([key,label,source])=><FilterSelect key={key} label={label} value={productFilters[key]||''} onChange={value=>setProductFilters(current=>({...current,[key]:value}))} options={[{value:'',label:'All '+label},...(filterOptions[source]||[]).map(item=>({value:item.id,label:item.name}))]} />)}
        </div>
        <button type="button" className="mt-4 rounded-xl border px-4 py-2 text-sm font-semibold text-primary-800" onClick={()=>{setProductFilters({});setStoreId('');setCategoryId('');setPaymentMethod('');}}>Reset filters</button>
        <p className="mt-5 text-xs text-gray-400">
          Report period: {selectedRange.from} to {selectedRange.to}
        </p>
      </div>

      {isLoading ? (
        <div className="rounded-3xl bg-white py-24 text-center text-gray-500 shadow-sm">
          Loading live reports...
        </div>
      ) : dashboard ? (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
            {[...stats, ...channelStats].map((item) => (
              <div
                key={item.title}
                className="analytics-kpi relative overflow-hidden"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="min-h-[2.5rem] text-xs font-semibold uppercase tracking-wide text-slate-500">{item.title}</p>

                    <h2 className="mt-1 break-words text-2xl font-bold tabular-nums tracking-tight text-slate-900">
                      {item.value}
                    </h2>

                    <p className="mt-2 min-h-[2rem] text-xs leading-4 text-slate-500">{item.detail}</p>

                    <p
                      className={`mt-1 text-xs font-semibold ${item.comparison.className}`}
                    >
                      {item.comparison.label}
                    </p>
                  </div>

                  <div
                    className="analytics-icon flex shrink-0 items-center justify-center"
                  >
                    {item.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <ChannelAnalytics data={metrics.purchases} charts={[["Purchase trend","trend"],["Leading suppliers","suppliers"],["Purchases by brand","brands"]]} />
            <ChannelAnalytics data={metrics.ecommerce} charts={[["Online order trend","trend"],["Online order status","statuses"],["Online payment status","payments"],["Leading online products","products"]]} />
          </div>
          <p className="mt-3 text-xs text-gray-500">Date and product filters apply across all channels. When product filters are selected, values use matching item lines and exclude order-level freight/shipping. Store filters apply to POS and purchases; online orders include all fulfilment locations. Payment filters apply to POS.</p>
          <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-12">
            <div className="xl:col-span-8 rounded-3xl bg-white p-6 shadow-sm">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-primary-800">
                  Sales Trend
                </h2>

                <p className="text-sm text-gray-500">
                  Daily POS revenue before period returns.
                </p>
              </div>

              <ChartEmpty hasData={salesTrend.length}>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={salesTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatMoney(value)} />
                    <Line
                      type="monotone"
                      dataKey="sales"
                      stroke="#7A2238"
                      strokeWidth={4}
                      dot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartEmpty>
            </div>

            <div className="xl:col-span-4 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-2 text-2xl font-bold text-primary-800">
                Sales By Category
              </h2>

              <p className="mb-4 text-sm text-gray-500">
                Based on POS sale line values.
              </p>

              <ChartEmpty hasData={dashboard.categorySales.length}>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={dashboard.categorySales}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={95}
                    >
                      {dashboard.categorySales.map((entry, index) => (
                        <Cell
                          key={`${entry.name}-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Legend />
                    <Tooltip formatter={(value) => formatMoney(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartEmpty>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
            <div className="xl:col-span-8 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="text-2xl font-bold text-primary-800">
                Revenue Overview
              </h2>

              <p className="mb-6 text-sm text-gray-500">
                Finance expenses and profit will appear after the accounting
                ledger.
              </p>

              <ChartEmpty hasData={salesTrend.length}>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={salesTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatMoney(value)} />
                    <Legend />
                    <Bar
                      dataKey="sales"
                      name="POS Revenue"
                      fill="#7A2238"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartEmpty>
            </div>

            <div className="xl:col-span-4 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-2xl font-bold text-primary-800">
                Payment Methods
              </h2>

              <ChartEmpty hasData={dashboard.paymentMethods.length}>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={dashboard.paymentMethods}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={95}
                    >
                      {dashboard.paymentMethods.map((entry, index) => (
                        <Cell
                          key={`${entry.name}-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Tooltip formatter={(value) => formatMoney(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </ChartEmpty>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
            <section className="xl:col-span-7 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-2xl font-bold text-primary-800">
                Top Selling Products
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px]">
                  <thead>
                    <tr className="border-b text-sm text-gray-500">
                      <th className="py-3 text-left">Product</th>
                      <th className="text-center">Sold</th>
                      <th className="text-center">Revenue</th>
                      <th className="text-center">Available Stock</th>
                    </tr>
                  </thead>

                  <tbody>
                    {dashboard.topProducts.map((item) => (
                      <tr
                        key={item.variantId}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-4">
                          <p className="font-semibold">{item.name}</p>
                          <p className="text-sm text-gray-500">{item.sku}</p>
                        </td>

                        <td className="text-center">
                          {formatNumber(item.sold)}
                        </td>
                        <td className="text-center">
                          {formatMoney(item.revenue)}
                        </td>
                        <td className="text-center">
                          {formatNumber(item.stock)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="xl:col-span-5 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-2xl font-bold text-primary-800">
                Top Customers
              </h2>

              <div className="space-y-5">
                {dashboard.topCustomers.length ? (
                  dashboard.topCustomers.map((customer) => (
                    <div
                      key={customer.name}
                      className="flex items-center justify-between rounded-2xl bg-gray-50 p-4"
                    >
                      <div>
                        <h3 className="font-semibold">{customer.name}</h3>
                        <p className="text-sm text-gray-500">
                          {customer.orders} Orders
                        </p>
                      </div>

                      <p className="font-bold text-primary-800">
                        {formatMoney(customer.spent)}
                      </p>
                    </div>
                  ))
                ) : (
                  <EmptyText text="No customer sales found for this period." />
                )}
              </div>
            </section>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
            <section className="xl:col-span-4 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-5 text-2xl font-bold text-primary-800">
                Low Stock
              </h2>

              <div className="space-y-4">
                {dashboard.lowStock.length ? (
                  dashboard.lowStock.map((item) => (
                    <div
                      key={`${item.variantId}-${item.sku}`}
                      className="flex items-center justify-between rounded-xl bg-red-50 p-4"
                    >
                      <div>
                        <p className="font-medium">{item.product}</p>
                        <p className="mt-1 text-xs text-gray-500">{item.sku}</p>
                      </div>

                      <div className="text-right">
                        <p className="font-bold text-red-600">
                          {formatNumber(item.availableQuantity)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Min: {formatNumber(item.minimumStock)}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <EmptyText text="No low-stock products in this selection." />
                )}
              </div>
            </section>

            <section className="xl:col-span-8 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-6 text-2xl font-bold text-primary-800">
                Recent POS Transactions
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[650px]">
                  <thead>
                    <tr className="border-b text-sm text-gray-500">
                      <th className="py-3 text-left">Receipt</th>
                      <th className="text-center">Customer</th>
                      <th className="text-center">Store</th>
                      <th className="text-center">Payment</th>
                      <th className="text-center">Amount</th>
                    </tr>
                  </thead>

                  <tbody>
                    {dashboard.recentTransactions.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-4 font-medium">{item.invoice}</td>
                        <td className="text-center">{item.customer}</td>
                        <td className="text-center">{item.store}</td>
                        <td className="text-center">{item.payment}</td>
                        <td className="text-center font-semibold">
                          {formatMoney(item.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
              <h2 className="mb-1 text-xl font-bold text-primary-800">Revenue by store</h2>
              <p className="mb-5 text-sm text-gray-500">Compare store sales in the selected period.</p>
              <ChartEmpty hasData={dashboard.storeSales?.length}><ResponsiveContainer width="100%" height={300}><BarChart data={dashboard.storeSales || []} layout="vertical" margin={{left:20,right:20}}><CartesianGrid strokeDasharray="3 3" /><XAxis type="number" tickFormatter={value=>formatMoney(value)} /><YAxis type="category" dataKey="name" width={110} /><Tooltip formatter={value=>formatMoney(value)} /><Bar dataKey="value" name="Revenue" fill="#7A2238" radius={[0,5,5,0]} /></BarChart></ResponsiveContainer></ChartEmpty>
            </section>
            <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
              <h2 className="mb-1 text-xl font-bold text-primary-800">Leading products by revenue</h2>
              <p className="mb-5 text-sm text-gray-500">The five highest-revenue product variants.</p>
              <ChartEmpty hasData={dashboard.topProducts?.length}><ResponsiveContainer width="100%" height={300}><BarChart data={dashboard.topProducts || []}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" tick={{fontSize:11}} /><YAxis tickFormatter={value=>formatMoney(value)} /><Tooltip formatter={value=>formatMoney(value)} /><Bar dataKey="revenue" name="Revenue" fill="#D4AF37" radius={[5,5,0,0]} /></BarChart></ResponsiveContainer></ChartEmpty>
            </section>
          </div>
          <section className="mt-6 rounded-3xl bg-white p-6 shadow-sm">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-primary-800">
                Quick Reports
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Open a detailed report for each business area.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-5 md:grid-cols-3 xl:grid-cols-5">
              {[
                {
                  title: "Sales",
                  subtitle: "Revenue, orders & stores",
                  icon: FiBarChart2,
                  path: "/admindashboard/reports/sales",
                  color: "bg-green-50 text-green-700",
                },
                {
                  title: "Customers",
                  subtitle: "Customers & spending",
                  icon: FiUsers,
                  path: "/admindashboard/reports/customers",
                  color: "bg-blue-50 text-blue-700",
                },
                {
                  title: "Inventory",
                  subtitle: "Stock & valuation",
                  icon: FiPackage,
                  path: "/admindashboard/reports/inventory",
                  color: "bg-purple-50 text-purple-700",
                },
                {
                  title: "Purchases",
                  subtitle: "GRNs & suppliers",
                  icon: FiTruck,
                  path: "/admindashboard/reports/purchases",
                  color: "bg-orange-50 text-orange-700",
                },
                {
                  title: "Employees",
                  subtitle: "Workforce insights",
                  icon: FiBriefcase,
                  path: "/admindashboard/reports/employees",
                  color: "bg-cyan-50 text-cyan-700",
                },
                {
                  title: "Attendance",
                  subtitle: "Presence & absences",
                  icon: FiCalendar,
                  path: "/admindashboard/reports/attendance",
                  color: "bg-yellow-50 text-yellow-700",
                },
                {
                  title: "Payroll",
                  subtitle: "Salary & payouts",
                  icon: FiCreditCard,
                  path: "/admindashboard/reports/payroll",
                  color: "bg-rose-50 text-rose-700",
                },
                {
                  title: "POS",
                  subtitle: "Counter-wise sales",
                  icon: FiMonitor,
                  path: "/admindashboard/reports/pos",
                  color: "bg-indigo-50 text-indigo-700",
                },
                {
                  title: "Finance",
                  subtitle: "Income & expenses",
                  icon: FiDollarSign,
                  path: "/admindashboard/analytics/finance",
                  color: "bg-emerald-50 text-emerald-700",
                },
                {
                  title: "GST",
                  subtitle: "Tax summary & returns",
                  icon: FiFileText,
                  path: "/admindashboard/reports/gst",
                  color: "bg-red-50 text-red-700",
                },
              ].filter(item => user?.roles?.includes('SUPER_ADMIN') || user?.permissions?.includes('analytics.' + item.title.toLowerCase())).map((item) => {
                const Icon = item.icon;
                const available = Boolean(item.path);

                return (
                  <button
                    key={item.title}
                    type="button"
                    disabled={!available}
                    onClick={() => available && navigate(item.path)}
                    className={`group rounded-2xl border border-gray-100 bg-gray-50 p-5 text-left transition-all ${
                      available
                        ? "hover:-translate-y-1 hover:border-primary-200 hover:bg-white hover:shadow-lg"
                        : "cursor-not-allowed opacity-60"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div
                        className={`flex h-11 w-11 items-center justify-center rounded-xl ${item.color}`}
                      >
                        <Icon size={20} />
                      </div>

                      {available ? (
                        <FiArrowRight className="mt-1 text-gray-300 transition group-hover:translate-x-1 group-hover:text-primary-700" />
                      ) : (
                        <span className="rounded-full bg-gray-200 px-2 py-1 text-[10px] font-semibold text-gray-500">
                          Soon
                        </span>
                      )}
                    </div>

                    <h3 className="mt-4 font-semibold text-gray-900">
                      {item.title}
                    </h3>

                    <p className="mt-1 text-xs font-normal text-gray-500">
                      {item.subtitle}
                    </p>
                  </button>
                );
              })}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium">{label}</label>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-gray-200 bg-white p-3 focus:outline-none focus:ring-2 focus:ring-primary-200"
      >
        {options.map((option) => {
          const normalized =
            typeof option === "string"
              ? { value: option, label: option }
              : option;

          return (
            <option key={normalized.value} value={normalized.value}>
              {normalized.label}
            </option>
          );
        })}
      </select>
    </div>
  );
}

function ChannelAnalytics({ data, charts }) {
  if (!data) return null;
  return <div className="contents">
    {charts.map(([label,key])=><div key={key} className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><div className="mb-5 flex items-center gap-3 border-b border-slate-100 pb-4"><span className="h-5 w-1 rounded bg-primary-800"/><h3 className="font-semibold text-slate-900">{label}</h3></div><ChartEmpty hasData={data[key]?.length}><ResponsiveContainer width="100%" height={300}>{key==='trend' ? <LineChart data={data[key]} margin={{left:0,right:20,bottom:5}}><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0"/><XAxis dataKey="name" tick={{fontSize:11}} tickLine={false} axisLine={false} minTickGap={25}/><YAxis tick={{fontSize:11}} tickLine={false} axisLine={false}/><Tooltip contentStyle={{borderRadius:12,borderColor:'#e2e8f0'}} formatter={value=>formatMoney(value)}/><Line type="monotone" dataKey="value" name="Value" stroke="#7A2238" strokeWidth={3} dot={false} activeDot={{r:5}}/></LineChart> : <BarChart data={data[key]} layout="vertical" margin={{left:10,right:20}}><CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0"/><XAxis type="number" tick={{fontSize:11}} tickLine={false} axisLine={false}/><YAxis type="category" dataKey="name" width={130} tick={{fontSize:11}} tickLine={false} axisLine={false}/><Tooltip contentStyle={{borderRadius:12,borderColor:'#e2e8f0'}} formatter={value=>['statuses','payments'].includes(key)?formatNumber(value):formatMoney(value)}/><Bar dataKey="value" name={['statuses','payments'].includes(key)?'Orders':'Value'} fill="#7A2238" maxBarSize={24} radius={[0,4,4,0]}/></BarChart>}</ResponsiveContainer></ChartEmpty></div>)}
  </div>;
}

function ChartEmpty({ hasData, children }) {
  if (hasData) return children;

  return (
    <div className="flex h-[300px] items-center justify-center text-sm text-gray-400">
      No report data for this period.
    </div>
  );
}

function EmptyText({ text }) {
  return <p className="py-8 text-center text-sm text-gray-400">{text}</p>;
}
